import { useEffect } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import Button from '@cloudscape-design/components/button'
import Container from '@cloudscape-design/components/container'
import Form from '@cloudscape-design/components/form'
import FormField from '@cloudscape-design/components/form-field'
import Input from '@cloudscape-design/components/input'
import KeyValuePairs from '@cloudscape-design/components/key-value-pairs'
import SpaceBetween from '@cloudscape-design/components/space-between'
import Page from '@/components/common/Page'
import { useAccount, useUpdateAccount } from '@/hooks/useAccount'
import { updateAccountSchema } from '@/lib/schemas'
import type { UpdateAccountFormData } from '@/lib/schemas'

export default function AccountPage() {
  const { account } = useAccount()
  const { updateAccount, loading: updatingAccount } = useUpdateAccount()

  const { control, handleSubmit, formState: { errors }, reset } = useForm<UpdateAccountFormData>({
    resolver: zodResolver(updateAccountSchema),
    defaultValues: { displayName: account?.displayName ?? '', photoUrl: account?.photoUrl ?? '' },
  })

  useEffect(() => {
    reset({ displayName: account?.displayName ?? '', photoUrl: account?.photoUrl ?? '' })
  }, [account, reset])

  return (
    <Page
      title="Account Settings"
      helpContent={{
        header: 'Account Settings',
        body: (
          <div>
            <p>Manage your personal user profile and account information.</p>
            <h4>Settings:</h4>
            <ul style={{ marginLeft: 20 }}>
              <li><strong>Email</strong> - Your account email (read-only)</li>
              <li><strong>Display Name</strong> - How you appear in the system</li>
              <li><strong>Profile Photo</strong> - Your account avatar</li>
            </ul>
            <h4>Tips:</h4>
            <p>Keep your display name and photo current. Your email is the account identifier and cannot be changed here.</p>
          </div>
        ),
      }}
    >
      <Container>
        <Form>
          <SpaceBetween size="m">
            <KeyValuePairs items={[{ label: 'Email', value: account?.email }]} />
            <FormField label="Display Name" errorText={errors.displayName?.message}>
              <Controller
                name="displayName"
                control={control}
                render={({ field: { value, onChange, onBlur } }) => (
                  <Input value={value ?? ''} onChange={(e) => onChange(e.detail.value)} onBlur={onBlur} />
                )}
              />
            </FormField>
            <FormField label="Photo URL" errorText={errors.photoUrl?.message}>
              <Controller
                name="photoUrl"
                control={control}
                render={({ field: { value, onChange, onBlur } }) => (
                  <Input value={value ?? ''} onChange={(e) => onChange(e.detail.value)} onBlur={onBlur} />
                )}
              />
            </FormField>
            <Button variant="primary" loading={updatingAccount} onClick={() => { handleSubmit(updateAccount)() }}>Save</Button>
          </SpaceBetween>
        </Form>
      </Container>
    </Page>
  )
}
